import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { useDrawerLayoutContext } from "../../-DrawerLayoutContext";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useEffect } from "react";
import { checkPermissionsInLoader } from "@dmca/frontend/hooks";
import { PageHeader } from "@dmca/frontend/components/nav";

export const Route = createFileRoute("/_authed/_drawerLayout/admin/machinery")({
  component: RouteComponent,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("Admin::ManageMachinery", context);
    const { trpcQcProxy: trpc, qc } = context;
    await Promise.all([
      qc.prefetchQuery(trpc.machinery.getAll.queryOptions()),
      qc.prefetchQuery(trpc.plant.getAll.queryOptions()),
    ]);
  },
  head: () => ({
    meta: [{ title: "Manage Machinery | DNA Dashboard" }],
  }),
});

function RouteComponent() {
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const currentId = useMatches({
    select: (matches) => {
      const last = matches[matches.length - 1];
      if (last.id.includes("add-new")) return "add-new";
      return (last.params as any)?.id as string | undefined;
    },
  });

  const { data: allMachinery, isLoading } = useQuery(
    trpc.machinery.getAll.queryOptions()
  );

  const listItems = useMemo(
    () => [
      { label: "Add New Machine", value: "add-new" },
      ...(allMachinery
        ?.map((m) => ({ label: `${m.name}`, value: m.id }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []),
    ],
    [allMachinery]
  );

  const {
    selectedItem: [, setSelectedItem],
  } = useDrawerLayoutContext({
    listItems,
    isLoading,
    onItemSelected: (item: { label: string; value: string }) => {
      if (item.value === "add-new") {
        navigate({ to: "/admin/machinery/machines/add-new" });
      } else {
        navigate({
          to: "/admin/machinery/machines/$id",
          params: { id: item.value },
        });
      }
    },
  });

  useEffect(() => {
    const selected = listItems.find((x) => x.value === currentId);
    setSelectedItem(selected);
  }, [currentId, listItems, setSelectedItem]);

  return (
    <>
      <PageHeader>Manage Machinery</PageHeader>
      <Outlet />
    </>
  );
}
